import { IconMoon, IconSun } from "@tabler/icons-react";
import { useEffect, useState, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { isLightThemePath, setThemePreference, THEME_CHANGE_EVENT } from "../homeTheme";

// Light/dark switch (Dan 2026-10-16): a small sun/moon pill in the bottom
// right that only shows while the visitor is on the hero, then fades away
// so it never sits over the page. It shows only on pages that can go light
// (homeTheme.ts LIGHT_THEME_PATHS). The pick is saved per browser and beats
// the device setting.
//
// Mounted once in its own root from gatsby-browser (no shared page layout,
// and the server never renders it, so there is nothing to hydrate).

/** Past this much scroll (share of the window height) the hero is gone. */
const HERO_SHARE = 0.6;

function currentTheme(): "light" | "dark" {
  return document.documentElement.dataset.theme === "light" ? "light" : "dark";
}

function ThemeSwitch() {
  const [theme, setTheme] = useState(currentTheme);
  const [onLightPage, setOnLightPage] = useState(() => isLightThemePath(window.location.pathname));
  const [onHero, setOnHero] = useState(() => window.scrollY < window.innerHeight * HERO_SHARE);

  useEffect(() => {
    // The theme is re-applied after every page change, so this one event
    // also tells us the page changed.
    const sync = () => {
      setTheme(currentTheme());
      setOnLightPage(isLightThemePath(window.location.pathname));
      setOnHero(window.scrollY < window.innerHeight * HERO_SHARE);
    };
    const onScroll = () => setOnHero(window.scrollY < window.innerHeight * HERO_SHARE);
    window.addEventListener(THEME_CHANGE_EVENT, sync);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener(THEME_CHANGE_EVENT, sync);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  if (!onLightPage) return null;
  const shown = onHero;

  return (
    <div
      role="group"
      aria-label="Color theme"
      aria-hidden={!shown}
      className={`fixed bottom-5 right-5 z-[60] flex items-center gap-0.5 rounded-full border border-border bg-card/90 p-1 shadow-[0_1px_2px_rgba(0,0,0,0.12),0_10px_28px_-12px_rgba(0,0,0,0.45)] backdrop-blur-md transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none ${
        shown ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-2 opacity-0"
      }`}
    >
      <SwitchButton
        label="Light"
        active={theme === "light"}
        tabIndex={shown ? 0 : -1}
        onClick={() => setThemePreference("light")}
      >
        <IconSun size={16} stroke={1.9} aria-hidden />
      </SwitchButton>
      <SwitchButton
        label="Dark"
        active={theme === "dark"}
        tabIndex={shown ? 0 : -1}
        onClick={() => setThemePreference("dark")}
      >
        <IconMoon size={15} stroke={1.9} aria-hidden />
      </SwitchButton>
    </div>
  );
}

function SwitchButton(props: {
  label: string;
  active: boolean;
  tabIndex: number;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={`${props.label} mode`}
      aria-pressed={props.active}
      title={`${props.label} mode`}
      tabIndex={props.tabIndex}
      onClick={props.onClick}
      className={`flex h-8 w-8 cursor-pointer items-center justify-center rounded-full transition-colors ${
        props.active
          ? "bg-white/[0.1] text-text-primary"
          : "text-text-muted hover:text-text-primary"
      }`}
    >
      {props.children}
    </button>
  );
}

/** Called once from gatsby-browser's onInitialClientRender. */
export function mountThemeSwitch() {
  if (document.getElementById("eden-theme-switch")) return;
  const host = document.createElement("div");
  host.id = "eden-theme-switch";
  document.body.appendChild(host);
  createRoot(host).render(<ThemeSwitch />);
}
